import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  createMonitoringIcon,
  getMonitoringConfig,
  MONITORING_BADGE_STYLES,
} from "@/constants/monitoring";
import type { CartItem } from "../store/useCartStore";

interface CartCardProps {
  item: CartItem;
  onRemove: (itemId: string) => void;
}

/**
 * Format platform name for display
 * Capitalizes first letter and handles special cases
 */
function formatPlatformName(platform: string): string {
  const platformMap: Record<string, string> = {
    twitter: "X",
    facebook: "Facebook",
    instagram: "Instagram",
    youtube: "YouTube",
    reddit: "Reddit",
    tiktok: "TikTok",
  };

  return platformMap[platform.toLowerCase()] || platform;
}

export function CartCard({ item, onRemove }: CartCardProps) {
  const monitoringConfig = getMonitoringConfig(item.monitoringType);
  const badgeColor = MONITORING_BADGE_STYLES[item.monitoringType];
  const monitoringIcon = createMonitoringIcon(
    item.monitoringType,
    "text-[16px] text-white",
  );

  return (
    <div className="w-full gap-2 rounded-[8px] py-3">
      <div className="flex flex-row gap-2 px-2">
        <div className="flex h-7 w-7 items-start justify-center py-[7px]">
          <div
            className={cn(
              "flex h-7 w-7 items-center justify-center rounded-[8px] p-1",
              badgeColor,
            )}
          >
            {monitoringIcon}
          </div>
        </div>
        <div className="flex flex-1 flex-row justify-between gap-2">
          <div className="flex-1 space-y-3">
            <div className="space-y-2">
              <div className="space-y-0.5">
                <h1 className="text-md leading-none font-extrabold tracking-normal text-neutral-900">
                  {item.planTitle}
                </h1>
                <div className="flex flex-row gap-1.5">
                  <p className="text-sm font-medium -tracking-wide text-neutral-700 capitalize">
                    {monitoringConfig.label.replace(" Monitoring", "")}
                  </p>
                  <p className="text-sm font-medium -tracking-wide text-neutral-300">
                    •
                  </p>
                  <p className="text-sm font-medium -tracking-wide text-neutral-700">
                    {item.selectedPlatforms.length} Platform
                    {item.selectedPlatforms.length > 1 ? "s" : ""}
                  </p>
                </div>
                <div className="flex flex-row flex-wrap gap-1">
                  {item.selectedPlatforms.map((platform) => (
                    <Badge
                      key={platform}
                      variant="outline"
                      className="border-neutral-200 px-2 py-1 text-neutral-800"
                    >
                      {formatPlatformName(platform)}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
            <h1 className="text-md font-medium text-neutral-900">
              {item.planPrice}
            </h1>
          </div>
        </div>
        <div className="flex h-7 w-7 items-start justify-center py-[7px]">
          <button
            onClick={() => onRemove(item.id)}
            className="py-[7px] transition-colors hover:text-red-500"
            aria-label="Remove item from cart"
          >
            <i className="ri-delete-bin-line text-[16px] font-light text-neutral-700 hover:text-red-500"></i>
          </button>
        </div>
      </div>
    </div>
  );
}
